import { db } from "@/db/schema";

/**
 * A local-storage inventory for the Privacy Inspector: what's stored, grouped by
 * category, with per-item drill-down. Read-only except for the explicit
 * `deleteItem` helper. Everything is local (IndexedDB + a couple of localStorage
 * keys); nothing is transmitted.
 */

export interface StorageItem {
  id: string;
  label: string;
  bytes: number;
  lastModified: string | null;
}

export interface StorageCategory {
  key: string;
  label: string;
  icon: string;
  count: number;
  bytes: number;
  items: StorageItem[];
  managePath: string | null;
}

const CONFIG_KEY = "catalogkit.config";
const THEME_KEY = "catalogkit.theme";

function jsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function localBytes(key: string): number {
  const value = safeGet(key);
  return value === null ? 0 : new TextEncoder().encode(value).length;
}

export async function getStorageInventory(): Promise<StorageCategory[]> {
  const [profiles, snapshots, quotes, audit, searches] = await Promise.all([
    db.profiles.toArray(),
    db.snapshots.toArray(),
    db.quotes.toArray(),
    db.auditLog.toArray(),
    db.savedSearches.toArray(),
  ]);

  const snapBytes = (s: (typeof snapshots)[number]): number =>
    s.rows_compressed.byteLength + jsonBytes({ ...s, rows_compressed: null });

  const configPresent = safeGet(CONFIG_KEY) !== null;
  const themePresent = safeGet(THEME_KEY) !== null;

  return [
    {
      key: "profiles",
      label: "Profiles",
      icon: "folder",
      managePath: "/profiles",
      count: profiles.length,
      bytes: profiles.reduce((n, p) => n + jsonBytes(p), 0),
      items: profiles.map((p) => ({
        id: p.id,
        label: p.label,
        bytes: jsonBytes(p),
        lastModified: p.last_used_at,
      })),
    },
    {
      key: "snapshots",
      label: "Snapshots",
      icon: "camera",
      managePath: "/profiles",
      count: snapshots.length,
      bytes: snapshots.reduce((n, s) => n + snapBytes(s), 0),
      items: snapshots.map((s) => ({
        id: s.id,
        label: s.label ?? s.source_filename,
        bytes: snapBytes(s),
        lastModified: s.uploaded_at,
      })),
    },
    {
      key: "quotes",
      label: "Quotes",
      icon: "file-text",
      managePath: "/quotes",
      count: quotes.length,
      bytes: quotes.reduce((n, q) => n + jsonBytes(q), 0),
      items: quotes.map((q) => ({
        id: q.id,
        label: q.customer_name,
        bytes: jsonBytes(q),
        lastModified: q.updated_at,
      })),
    },
    {
      key: "auditLog",
      label: "Audit Log",
      icon: "list-details",
      managePath: "/audit-log",
      count: audit.length,
      bytes: audit.reduce((n, a) => n + jsonBytes(a), 0),
      items: audit.slice(0, 100).map((a) => ({
        id: a.id,
        label: a.action,
        bytes: jsonBytes(a),
        lastModified: a.timestamp,
      })),
    },
    {
      key: "savedSearches",
      label: "Saved Searches",
      icon: "bookmark",
      managePath: "/saved-searches",
      count: searches.length,
      bytes: searches.reduce((n, s) => n + jsonBytes(s), 0),
      items: searches.map((s) => ({
        id: s.id,
        label: s.label,
        bytes: jsonBytes(s),
        lastModified: s.created_at,
      })),
    },
    {
      key: "config",
      label: "Configuration",
      icon: "settings",
      managePath: "/settings",
      count: configPresent ? 1 : 0,
      bytes: localBytes(CONFIG_KEY),
      items: configPresent
        ? [{ id: CONFIG_KEY, label: "App configuration", bytes: localBytes(CONFIG_KEY), lastModified: null }]
        : [],
    },
    {
      key: "theme",
      label: "Theme Preference",
      icon: "palette",
      managePath: "/settings",
      count: themePresent ? 1 : 0,
      bytes: localBytes(THEME_KEY),
      items: themePresent
        ? [{ id: THEME_KEY, label: "Theme", bytes: localBytes(THEME_KEY), lastModified: null }]
        : [],
    },
  ];
}

function pretty(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

/** JSON for one item (snapshot payload is summarized, not dumped). */
export async function getItemJson(categoryKey: string, id: string): Promise<string> {
  switch (categoryKey) {
    case "profiles":
      return pretty(await db.profiles.get(id));
    case "snapshots": {
      const snapshot = await db.snapshots.get(id);
      return pretty(
        snapshot
          ? { ...snapshot, rows_compressed: `<${snapshot.rows_compressed.byteLength} bytes gzip>` }
          : null,
      );
    }
    case "quotes":
      return pretty(await db.quotes.get(id));
    case "auditLog":
      return pretty(await db.auditLog.get(id));
    case "savedSearches":
      return pretty(await db.savedSearches.get(id));
    case "config":
    case "theme":
      return safeGet(id) ?? "null";
    default:
      return "null";
  }
}

/** Delete one item. Deleting a profile cascades to its snapshots. */
export async function deleteItem(categoryKey: string, id: string): Promise<void> {
  switch (categoryKey) {
    case "profiles":
      await db.snapshots.where("profile_id").equals(id).delete();
      await db.profiles.delete(id);
      break;
    case "snapshots":
      await db.snapshots.delete(id);
      break;
    case "quotes":
      await db.quotes.delete(id);
      break;
    case "auditLog":
      await db.auditLog.delete(id);
      break;
    case "savedSearches":
      await db.savedSearches.delete(id);
      break;
    case "config":
    case "theme":
      try {
        localStorage.removeItem(id);
      } catch {
        // ignore
      }
      break;
  }
}
